import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RejectedIcon } from '../../../../../../constructs/pipeline/badge'
import { SelectionCheckbox } from '../../../selection-checkbox'
import { CountBadge } from './count-badge'
import { DraggableCard } from './draggable-card'

/**
 * Props for the RejectedDrawer component
 * Generic type T represents the candidate data type
 */
export interface RejectedDrawerProps<T> {
	/** Array of rejected candidates to display */
	candidates: T[]
	/** Function to render the content of each candidate card */
	renderCard: (candidate: T) => React.ReactNode
	/** Function to extract the unique ID from a candidate */
	getCandidateId: (candidate: T) => string
	/** Set of currently selected candidate IDs */
	selectedIds: Set<string>
	/** Callback to toggle selection of a candidate */
	toggleSelect: (id: string) => void
	/** Callback when a card body is clicked (opens profile) */
	handleCardClick: (candidate: T) => void
	/** ID of the candidate currently being dragged, if any */
	draggingId: string | null
	/** ID of the rejected stage */
	rejectedStageId: string
	/** Resolves a candidate's accessible selection-checkbox label */
	selectLabelFor: (candidate: T) => string
	/** Callback to select/deselect all candidates in the drawer */
	onSelectAll?: () => void
	/** Whether all candidates in the drawer are selected */
	selectAllChecked?: boolean
	/** Whether some (but not all) candidates in the drawer are selected */
	selectAllIndeterminate?: boolean
}

export function RejectedDrawer<T>({
	candidates,
	renderCard,
	getCandidateId,
	selectedIds,
	toggleSelect,
	handleCardClick,
	draggingId,
	rejectedStageId,
	selectLabelFor,
	onSelectAll,
	selectAllChecked = false,
	selectAllIndeterminate = false,
}: RejectedDrawerProps<T>) {
	const { t } = useTranslation('common')
	const drawerRef = useRef<HTMLDivElement>(null)
	const [isOver, setIsOver] = useState(false)

	useEffect(() => {
		const element = drawerRef.current
		if (!element) { return }

		return dropTargetForElements({
			element,
			getData: () => ({ stageId: rejectedStageId }),
			onDragEnter: () => setIsOver(true),
			onDragLeave: () => setIsOver(false),
			onDrop: () => setIsOver(false),
		})
	}, [rejectedStageId])

	return (
		<div className="h-[120px]">
			<div
				ref={drawerRef}
				className={`h-[250px] border-t-2 border-red-700 backdrop-blur-sm overflow-y-auto overflow-x-hidden p-4 transition-all duration-500 hover:translate-y-[-130px] ${
					isOver ? 'bg-red-100/50 translate-y-[-130px]' : 'bg-red-50/50'
				}`}
			>
				<h2 className="group text-lg font-semibold text-red-900 m-0 mb-4 flex items-center gap-2 select-none">
					{onSelectAll && candidates.length > 0 && (
						<span className="inline-flex">
							<SelectionCheckbox
								checked={selectAllChecked}
								indeterminate={selectAllIndeterminate}
								onToggle={() => onSelectAll()}
								label={`${t('bulkActions.selectAll')}: Rejected`}
								alwaysVisible
							/>
						</span>
					)}
					<RejectedIcon />
					Rejected
					<CountBadge count={candidates.length} className="bg-red-800/20 text-red-900" />
				</h2>
				<div className="flex gap-3 flex-wrap">
					{candidates.map((candidate) => {
						const id = getCandidateId(candidate)
						const isBeingDragged = draggingId === id
							|| (draggingId !== null && selectedIds.has(draggingId) && selectedIds.has(id))
						return (
							<DraggableCard
								key={id}
								candidateId={id}
								sourceStage={rejectedStageId}
								selected={selectedIds.has(id)}
								selectedCount={selectedIds.size}
								onSelect={() => toggleSelect(id)}
								onClick={() => handleCardClick(candidate)}
								isDragging={isBeingDragged}
								selectLabel={selectLabelFor(candidate)}
							>
								{renderCard(candidate)}
							</DraggableCard>
						)
					})}
				</div>
			</div>
		</div>
	)
}
